import { useState } from 'react';
import { Building2, ChevronRight, Shield, LogOut } from 'lucide-react';
import { Button, Spinner } from '@/core/ui/components';
import { useAuth, useToast } from '@/app/providers';

export function TenantSelectionPage() {
  const { tenants, selectTenant, logout, isLoading, user } = useAuth();
  const { showToast } = useToast();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const handleSelectTenant = async (tenantId: string) => {
    setSelectedId(tenantId);
    try {
      await selectTenant(tenantId);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to select tenant';
      showToast('error', message);
      setSelectedId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-indigo-600 mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">Select Tenant</h1>
          <p className="mt-2 text-slate-400">
            Welcome back, {user?.name || user?.email}
          </p>
        </div>

        {/* Tenant List */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-6">
          <p className="text-sm text-slate-400 mb-4">
            Choose a tenant to access:
          </p>

          <div className="space-y-3">
            {tenants.map((tenant) => (
              <button
                key={tenant.tenantId}
                onClick={() => handleSelectTenant(tenant.tenantId)}
                disabled={isLoading}
                className={`
                  w-full flex items-center gap-4 p-4 rounded-xl border transition-all
                  ${selectedId === tenant.tenantId
                    ? 'bg-indigo-600/20 border-indigo-500'
                    : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20'
                  }
                  disabled:opacity-60 disabled:cursor-not-allowed
                `}
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-indigo-500/20 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-indigo-400" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium text-white">{tenant.tenantName}</p>
                  <p className="text-sm text-slate-400 capitalize">{tenant.role}</p>
                </div>
                {selectedId === tenant.tenantId && isLoading ? (
                  <Spinner size="sm" className="text-white" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                )}
              </button>
            ))}
          </div>

          {tenants.length === 0 && (
            <div className="text-center py-8">
              <Building2 className="w-12 h-12 text-slate-500 mx-auto mb-3" />
              <p className="text-slate-400">No tenants available</p>
              <p className="text-sm text-slate-500 mt-1">
                Contact support if you believe this is an error
              </p>
            </div>
          )}
        </div>

        {/* Logout Button */}
        <div className="mt-6 text-center">
          <Button
            variant="ghost"
            onClick={logout}
            leftIcon={<LogOut className="w-4 h-4" />}
            className="text-slate-400 hover:text-white hover:bg-white/10"
          >
            Sign out
          </Button>
        </div>
      </div>
    </div>
  );
}
